/**
 * Turns the flat `{path: {old, new}}` map the history service returns into the
 * shape the drawer renders: sections, plain lines, and collapsed array groups.
 *
 * Two collapses do the heavy lifting, both measured against the real data:
 *
 * - Array groups. A company save rewrites `calculationSettings.workTypes`, which
 *   expands to roughly 590 leaf lines per record — 23 of 35 company entries in
 *   the local database exceed 50 lines from this one array. Grouped by element,
 *   that reads as one line naming how many items moved.
 * - Reference subtrees. An employee snapshot carries the whole company, sector
 *   and author documents inline: about 300 of its 438 leaf paths describe some
 *   other record. Those fold into a single line naming the reference.
 */

import {
  HISTORY_SECTION,
  SECTION_ORDER,
  HEADER_FIELDS,
  describeField,
  getReferenceRoot,
  normalizeFieldPath,
} from './fieldDictionary';
import { formatHistoryChange } from './formatHistoryValue';

/** Above this many changed elements, an array renders as a summary line. */
export const ARRAY_COLLAPSE_LIMIT = 3;

/** Fields good enough to name an array element in a summary. */
const ITEM_LABEL_FIELDS = [
  'label',
  'name',
  'title',
  'sectorName',
  'originalName',
  'selectedDate',
  'key',
  'code',
  'value',
];

const ARRAY_PATH = /^(.*?)\[(\d+)\](?:\.(.+))?$/;

/**
 * Splits `workTypes[3].color.argb` into its array root, index and remainder.
 *
 * @param {string} path - Field path.
 * @returns {{root: string, index: number, rest: string}|null}
 */
const matchArrayPath = (path) => {
  const match = ARRAY_PATH.exec(path);
  if (!match) return null;
  return { root: match[1], index: Number(match[2]), rest: match[3] ?? '' };
};

/**
 * Resolves the label for a field path.
 *
 * @param {Object} descriptor - Result of `describeField`.
 * @param {Function} t - i18next `t` bound to the `history` namespace.
 * @returns {string}
 */
const labelFor = (descriptor, t) => {
  if (!descriptor.i18nKey) return descriptor.path;

  const base = t(`field.${descriptor.i18nKey}`, {
    defaultValue: descriptor.path,
    ...descriptor.params,
  });

  // A variation entry can hold several fields ("… · iznos", "… · važi od").
  // The detail is a separate key rather than part of the label, so one string
  // per family covers every leaf and every company code.
  if (!descriptor.detailKey) return base;

  const detail = descriptor.detailRaw
    ? descriptor.detailRaw
    : t(`field.${descriptor.detailKey}`, { defaultValue: descriptor.detailKey });

  return `${base} · ${detail}`;
};

/**
 * Builds one rendered change line.
 *
 * @param {string} modelName - Model the record belongs to.
 * @param {string} path - Field path.
 * @param {Object} change - `{ old, new }`.
 * @param {Object} options - `{ t, locale }`.
 * @returns {Object} A line ready to render.
 */
const buildLine = (modelName, path, change, options) => {
  const descriptor = describeField(modelName, path);
  const { from, to } = formatHistoryChange(change, descriptor, options);

  return {
    id: path,
    path,
    descriptor,
    label: labelFor(descriptor, options.t),
    section: descriptor.section,
    known: descriptor.known,
    from,
    to,
  };
};

/**
 * Names one element of an array from whichever of its changed fields reads best,
 * falling back to its position.
 *
 * @param {Object} item - Accumulated element: `{ index, entries }`.
 * @param {Function} t - Bound `t`.
 * @returns {string}
 */
const labelForArrayItem = (item, t) => {
  for (const candidate of ITEM_LABEL_FIELDS) {
    const entry = item.entries.find((e) => e.rest === candidate);
    if (!entry) continue;
    // The element's own name is more useful as a heading than as a change line,
    // and the previous value names the element as it was known.
    const value = entry.change.new ?? entry.change.old;
    if (value !== null && value !== undefined && value !== '') {
      return String(value);
    }
  }
  return t('value.itemPosition', {
    index: item.index + 1,
    defaultValue: `#${item.index + 1}`,
  });
};

/**
 * Groups the change map into sections, collapsing arrays and populated
 * references.
 *
 * @param {Object} changes - `{path: {old, new}}` from the history service.
 * @param {string} modelName - Model the record belongs to.
 * @param {Object} options - `{ t, locale }`, where `t` is bound to `history`.
 * @returns {{sections: Array, totalLines: number, headline: Object|null, summary: Array}}
 */
export const groupHistoryChanges = (changes, modelName, options) => {
  const { t } = options;
  const entries = Object.entries(changes || {});

  const plainLines = [];
  const arrayGroups = new Map();
  const referenceGroups = new Map();

  for (const [rawPath, change] of entries) {
    const path = String(rawPath);
    const normalized = normalizeFieldPath(path);

    if (HEADER_FIELDS.includes(normalized)) continue;

    // 🚨 A write that reaches the audit service as `{$pull: …}` is recorded
    // under the operator itself, so the trail carries paths named `$pull` and
    // `$addToSet`. They name no field and there is nothing to say about them;
    // the fields they touched are recorded by their own entries.
    if (normalized.startsWith('$')) continue;

    const referenceRoot = getReferenceRoot(modelName, path);
    if (referenceRoot) {
      if (!referenceGroups.has(referenceRoot)) {
        referenceGroups.set(referenceRoot, []);
      }
      referenceGroups.get(referenceRoot).push({ path, change });
      continue;
    }

    const arrayMatch = matchArrayPath(path);
    // 🚨 `rest` is empty when the element itself changed rather than one of its
    // fields — `workTypes[7]`, not `workTypes[7].label`. Those used to fall
    // through to plain lines, and a company whose twenty work types were
    // renamed printed twenty consecutive rows all headed
    // `calculationSettings.workTypes[]`. They belong to the array like any
    // other element change.
    if (arrayMatch) {
      const { root, index, rest } = arrayMatch;
      if (!arrayGroups.has(root)) arrayGroups.set(root, new Map());
      const items = arrayGroups.get(root);
      if (!items.has(index)) items.set(index, { index, entries: [] });
      items.get(index).entries.push({ path, rest, change });
      continue;
    }

    plainLines.push(buildLine(modelName, path, change, options));
  }

  const sections = new Map();

  /**
   * Files a rendered block under its section, creating the section on first use.
   *
   * @param {string} sectionKey - One of HISTORY_SECTION.
   * @param {string} bucket - `lines`, `arrays` or `references`.
   * @param {Object} block - The block to append.
   */
  const push = (sectionKey, bucket, block) => {
    if (!sections.has(sectionKey)) {
      sections.set(sectionKey, {
        key: sectionKey,
        label: t(`section.${sectionKey}`, { defaultValue: sectionKey }),
        lines: [],
        arrays: [],
        references: [],
      });
    }
    sections.get(sectionKey)[bucket].push(block);
  };

  for (const line of plainLines) push(line.section, 'lines', line);

  // Array groups: one block per array, holding its changed elements.
  for (const [root, itemMap] of arrayGroups) {
    const descriptor = describeField(modelName, root);
    const items = [...itemMap.values()]
      .sort((a, b) => a.index - b.index)
      .map((item) => ({
        index: item.index,
        label: labelForArrayItem(item, t),
        lines: item.entries.map(({ path, change }) =>
          buildLine(modelName, path, change, options)
        ),
      }));

    const lineCount = items.reduce((sum, item) => sum + item.lines.length, 0);

    push(descriptor.section, 'arrays', {
      id: root,
      path: root,
      label: labelFor(descriptor, t),
      items,
      itemCount: items.length,
      lineCount,
      collapsed: items.length > ARRAY_COLLAPSE_LIMIT,
      summary: t('value.itemsChanged', {
        count: items.length,
        defaultValue: `${items.length}`,
      }),
    });
  }

  // Reference subtrees: one block per referenced record.
  for (const [root, refEntries] of referenceGroups) {
    const descriptor = describeField(modelName, root);
    push(descriptor.section, 'references', {
      id: root,
      path: root,
      label: labelFor(descriptor, t),
      lineCount: refEntries.length,
      summary: t('value.relatedChanged', {
        count: refEntries.length,
        defaultValue: `${refEntries.length}`,
      }),
      lines: refEntries.map(({ path, change }) =>
        buildLine(modelName, path, change, options)
      ),
    });
  }

  const orderedSections = [...sections.values()].sort((a, b) => {
    const ai = SECTION_ORDER.indexOf(a.key);
    const bi = SECTION_ORDER.indexOf(b.key);
    return (ai === -1 ? SECTION_ORDER.length : ai) - (bi === -1 ? SECTION_ORDER.length : bi);
  });

  const totalLines = orderedSections.reduce(
    (sum, section) =>
      sum +
      section.lines.length +
      section.arrays.reduce((n, group) => n + group.lineCount, 0) +
      section.references.reduce((n, group) => n + group.lineCount, 0),
    0
  );

  // The headline names the section that moved most, which is what a reader scans
  // for. Ties keep dictionary order, so "Godišnji odmor" wins over "Ostalo".
  const headlineSection = orderedSections.reduce((best, section) => {
    const weight =
      section.lines.length + section.arrays.length + section.references.length;
    const bestWeight = best
      ? best.lines.length + best.arrays.length + best.references.length
      : -1;
    return weight > bestWeight ? section : best;
  }, null);

  // Summary: the first couple of concrete lines, preferring described fields —
  // a raw path tells the reader less than a named one at a glance.
  const summaryCandidates = orderedSections.flatMap((section) => section.lines);
  const summary = [
    ...summaryCandidates.filter((line) => line.known),
    ...summaryCandidates.filter((line) => !line.known),
  ].slice(0, 2);

  const summaryArrays = orderedSections
    .flatMap((section) => [...section.arrays, ...section.references])
    .slice(0, summary.length ? 1 : 2);

  return {
    sections: orderedSections,
    totalLines,
    headline: headlineSection
      ? { key: headlineSection.key, label: headlineSection.label }
      : null,
    summary,
    summaryArrays,
  };
};

/**
 * Flattens a snapshot document into sectioned `label / value` rows.
 *
 * Populated references collapse to their name: showing a company's 200 settings
 * inside an employee snapshot buries the employee.
 *
 * @param {Object} data - Snapshot `data` as stored.
 * @param {string} modelName - Model the record belongs to.
 * @param {Object} options - `{ t, locale }`.
 * @returns {{sections: Array, totalFields: number}}
 */
export const groupSnapshotData = (data, modelName, options) => {
  const { t } = options;
  const rows = [];

  /**
   * Walks the snapshot, emitting one row per leaf and stopping at references.
   *
   * @param {*} value - Current node.
   * @param {string} prefix - Path accumulated so far.
   * @param {number} depth - Recursion guard.
   */
  const walk = (value, prefix, depth) => {
    if (depth > 6) return;

    if (value === null || value === undefined || typeof value !== 'object') {
      rows.push({ path: prefix, value });
      return;
    }

    if (value instanceof Date || Array.isArray(value)) {
      rows.push({ path: prefix, value });
      return;
    }

    // Enum objects and populated documents are leaves, not containers.
    if (value.label !== undefined || value.originalName !== undefined) {
      rows.push({ path: prefix, value });
      return;
    }

    const keys = Object.keys(value).filter((key) => key !== '_id' && key !== '__v');
    if (!keys.length) {
      rows.push({ path: prefix, value: null });
      return;
    }

    for (const key of keys) {
      const childPath = prefix ? `${prefix}.${key}` : key;
      // A populated reference is named, not unpacked.
      if (getReferenceRoot(modelName, `${childPath}.x`)) {
        rows.push({ path: childPath, value: value[key], isReference: true });
        continue;
      }
      walk(value[key], childPath, depth + 1);
    }
  };

  walk(data, '', 0);

  const sections = new Map();

  for (const row of rows) {
    if (!row.path) continue;
    const normalized = normalizeFieldPath(row.path);
    // A snapshot is the whole record, so author and timestamps belong in it —
    // unlike a change line, where the drawer header already carries them.
    if (['_id', '__v', 'updatedBy'].includes(normalized)) continue;

    const descriptor = describeField(modelName, row.path);
    const effective = row.isReference
      ? { ...descriptor, type: 'ref' }
      : descriptor;

    const { from } = formatHistoryChange({ old: row.value }, effective, options);

    if (!sections.has(descriptor.section)) {
      sections.set(descriptor.section, {
        key: descriptor.section,
        label: t(`section.${descriptor.section}`, {
          defaultValue: descriptor.section,
        }),
        rows: [],
      });
    }

    sections.get(descriptor.section).rows.push({
      id: row.path,
      path: row.path,
      label: labelFor(descriptor, t),
      value: from,
      known: descriptor.known,
    });
  }

  const orderedSections = [...sections.values()].sort((a, b) => {
    const ai = SECTION_ORDER.indexOf(a.key);
    const bi = SECTION_ORDER.indexOf(b.key);
    return (ai === -1 ? SECTION_ORDER.length : ai) - (bi === -1 ? SECTION_ORDER.length : bi);
  });

  return {
    sections: orderedSections,
    totalFields: orderedSections.reduce((sum, s) => sum + s.rows.length, 0),
  };
};

export { HISTORY_SECTION };
